import React from "react";
import { Link } from "react-router-dom";
import { Github, Linkedin, Instagram, Twitter, ArrowUpRight, ShieldCheck, Zap } from "lucide-react";
import Logo from "../assets/quivix.png";

const Footer = () => {
  return (
    <footer className="bg-black text-[#86868b] border-t border-white/[0.08] pt-16 pb-12 font-sans relative overflow-hidden">
      {/* Background Ambient Glow */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[250px] bg-blue-600/5 blur-[120px] pointer-events-none rounded-full" />

      <div className="max-w-6xl mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-10 pb-12 border-b border-white/[0.06]">
          
          {/* Brand Info (2 Columns) */}
          <div className="md:col-span-2 flex flex-col gap-4">
            <Link to="/" className="flex items-center gap-3 group w-fit">
              <div className="w-8 h-8 rounded-xl bg-neutral-900 border border-white/20 p-1 flex items-center justify-center shrink-0 shadow-lg group-hover:border-blue-400 transition-all duration-300">
                <img src={Logo} alt="QuivixDigital Logo" className="w-full h-full object-contain" />
              </div>
              <span className="text-white font-bold text-lg tracking-tight font-heading">QuivixDigital</span>
            </Link>

            <p className="text-xs text-[#86868b] leading-relaxed max-w-sm">
              Architects of high-performance consumer software, real-time networking systems, and intelligent digital platforms. Designed with uncompromised precision.
            </p>

            <div className="flex items-center gap-2 mt-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[11px] font-mono text-emerald-400/90 tracking-wide">
                All Systems Operational • 20-30ms Latency
              </span>
            </div>

            <div className="text-[11px] font-mono text-[#86868b] mt-1 space-y-1">
              <p>Support: <a href="mailto:support@quivixdigital.com" className="text-white hover:text-blue-400 transition-colors">support@quivixdigital.com</a></p>
              <p>Studio HQ: <span className="text-white">New Mexico, USA</span></p>
            </div>
          </div>

          {/* Platform */}
          <div className="flex flex-col gap-3">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-white font-heading">Ecosystem</h4>
            <ul className="flex flex-col gap-2.5 text-xs">
              <li><Link to="/apps" className="hover:text-white transition-colors">Ziro Engine</Link></li>
              <li><Link to="/apps" className="hover:text-white transition-colors">Chafadi Suite</Link></li>
              <li><Link to="/apps" className="hover:text-white transition-colors">Quivix Core AI</Link></li>
              <li><Link to="/invest" className="hover:text-white transition-colors flex items-center gap-1">Investor Portal <ArrowUpRight className="w-3 h-3 opacity-60" /></Link></li>
            </ul>
          </div>

          {/* Company */}
          <div className="flex flex-col gap-3">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-white font-heading">Company</h4>
            <ul className="flex flex-col gap-2.5 text-xs">
              <li><Link to="/about" className="hover:text-white transition-colors">About QuivixDigital</Link></li>
              <li><a href="https://careers.quivixdigital.com/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors flex items-center gap-1">Careers & Internships <ArrowUpRight className="w-3.5 h-3.5 text-sky-400 opacity-80 shrink-0" /></a></li>
              <li><Link to="/invest" className="hover:text-white transition-colors">Ecosystem Metrics</Link></li>
              <li><Link to="/contact" className="hover:text-white transition-colors">Direct Inquiries</Link></li>
            </ul>
          </div>

          {/* Governance & Security */}
          <div className="flex flex-col gap-3">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-white font-heading">Governance</h4>
            <ul className="flex flex-col gap-2.5 text-xs">
              <li><Link to="/ziro-privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
              <li><Link to="/chafadi-privacy-policy" className="hover:text-white transition-colors">Terms of Service</Link></li>
              <li><Link to="/ziro-delete-account" className="hover:text-white transition-colors">Data Retention</Link></li>
            </ul>

            <div className="flex items-center gap-3 mt-4 text-[#86868b]">
              <a href="https://github.com" target="_blank" rel="noreferrer" className="hover:text-white transition-colors p-1.5 bg-white/5 rounded-lg border border-white/10" aria-label="GitHub"><Github className="w-3.5 h-3.5" /></a>
              <a href="https://linkedin.com" target="_blank" rel="noreferrer" className="hover:text-white transition-colors p-1.5 bg-white/5 rounded-lg border border-white/10" aria-label="LinkedIn"><Linkedin className="w-3.5 h-3.5" /></a>
              <a href="https://twitter.com" target="_blank" rel="noreferrer" className="hover:text-white transition-colors p-1.5 bg-white/5 rounded-lg border border-white/10" aria-label="Twitter"><Twitter className="w-3.5 h-3.5" /></a>
              <a href="https://instagram.com" target="_blank" rel="noreferrer" className="hover:text-white transition-colors p-1.5 bg-white/5 rounded-lg border border-white/10" aria-label="Instagram"><Instagram className="w-3.5 h-3.5" /></a>
            </div>
          </div>

        </div>

        {/* Bottom Bar */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-[11px] text-[#86868b]">
          <p>© {new Date().getFullYear()} QuivixDigital. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> SOC2 Security Standard</span>
            <span className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-blue-400" /> Sub 25ms Engine</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
